import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

function initScrollReveal() {
  const els = Array.from(document.querySelectorAll<HTMLElement>('[data-rise],[data-fade],[data-clip],[data-clip-r]'));
  if (!els.length) return;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduced || !('IntersectionObserver' in window)) {
    els.forEach(el => el.classList.add('in'));
    return;
  }
  document.querySelectorAll<HTMLElement>('[data-stagger]').forEach(group => {
    const step = parseInt(group.getAttribute('data-stagger') || '70', 10);
    Array.from(group.querySelectorAll<HTMLElement>('[data-rise],[data-fade],[data-clip],[data-clip-r]')).forEach((child, i) => {
      if (!child.style.getPropertyValue('--d')) child.style.setProperty('--d', `${i * step}ms`);
    });
  });
  const io = new IntersectionObserver((entries) => {
    entries.forEach(e => {
      if (!e.isIntersecting) return;
      (e.target as HTMLElement).classList.add('in');
      io.unobserve(e.target);
    });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
  els.forEach(el => {
    const r = el.getBoundingClientRect();
    if (r.top < window.innerHeight * 0.9 && r.bottom > 0) el.classList.add('in');
    else io.observe(el);
  });
  setTimeout(() => els.forEach(el => el.classList.add('in')), 4000);
}

function initLineReveal() {
  const targets = Array.from(document.querySelectorAll<HTMLElement>('.reveal-lines'));
  if (!targets.length) return;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduced) { targets.forEach(t => t.classList.add('in')); return; }
  targets.forEach(el => {
    if (!el.getAttribute('data-src-html')) el.setAttribute('data-src-html', el.innerHTML);
  });
  const split = (el: HTMLElement) => {
    const src = el.getAttribute('data-src-html') || el.innerHTML;
    const probe = document.createElement('div');
    probe.innerHTML = src;
    if (probe.querySelector('br')) {
      const chunks = src.split(/<br\s*\/?>/i);
      el.innerHTML = chunks.map(c => `<span class="ln"><span>${c}</span></span>`).join('');
      return;
    }
    el.innerHTML = src;
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
    const textNodes: Text[] = []; let n: Node | null;
    // eslint-disable-next-line no-cond-assign
    while (n = walker.nextNode()) if (n.nodeValue && n.nodeValue.trim()) textNodes.push(n as Text);
    if (!textNodes.length) return;
    textNodes.forEach(node => {
      const frag = document.createDocumentFragment();
      node.nodeValue!.split(/(\s+)/).forEach(tok => {
        if (!tok) return;
        if (/^\s+$/.test(tok)) { frag.appendChild(document.createTextNode(tok)); return; }
        const s = document.createElement('span');
        s.className = '_w';
        s.textContent = tok;
        frag.appendChild(s);
      });
      node.parentNode!.replaceChild(frag, node);
    });
    const words = Array.from(el.querySelectorAll<HTMLElement>('._w'));
    if (!words.length) return;
    const lines: HTMLElement[][] = []; let last: number | null = null;
    words.forEach(w => {
      const top = Math.round(w.offsetTop);
      if (last === null || Math.abs(top - last) > 4) { lines.push([]); last = top; }
      lines[lines.length - 1].push(w);
    });
    el.innerHTML = lines.map(line => `<span class="ln"><span>${line.map(w => w.outerHTML).join(' ')}</span></span>`).join('');
    el.querySelectorAll<HTMLElement>('._w').forEach(w => (w.style.display = 'inline'));
  };
  targets.forEach(split);
  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (!e.isIntersecting) return;
        (e.target as HTMLElement).classList.add('in');
        io.unobserve(e.target);
      });
    }, { rootMargin: '0px 0px -6% 0px', threshold: 0.15 });
    targets.forEach(el => {
      const r = el.getBoundingClientRect();
      if (r.top < window.innerHeight * 0.92) setTimeout(() => el.classList.add('in'), 60);
      else io.observe(el);
    });
  } else {
    targets.forEach(t => t.classList.add('in'));
  }
  setTimeout(() => targets.forEach(t => t.classList.add('in')), 4000);
  let rt: any;
  window.addEventListener('resize', () => {
    clearTimeout(rt);
    rt = setTimeout(() => {
      targets.forEach(el => {
        const wasIn = el.classList.contains('in');
        split(el);
        if (wasIn) el.classList.add('in');
      });
    }, 220);
  });
}

function initCounters() {
  const counters = Array.from(document.querySelectorAll<HTMLElement>('[data-count-to]'));
  if (!counters.length) return;
  const render = (el: HTMLElement, v: number, dec: number) => {
    let s = dec > 0 ? v.toFixed(dec) : String(Math.round(v));
    if (el.getAttribute('data-count-sep') !== 'off' && Math.abs(v) >= 1000) {
      const parts = s.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      s = parts.join('.');
    }
    el.textContent = s;
  };
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduced || !('IntersectionObserver' in window)) {
    counters.forEach(el => {
      const t = parseFloat(el.getAttribute('data-count-to') || '0');
      render(el, t, (el.getAttribute('data-count-to')?.split('.')[1] || '').length);
    });
    return;
  }
  counters.forEach(el => {
    const raw = el.getAttribute('data-count-to') || '0';
    const target = parseFloat(raw);
    const dec = (raw.split('.')[1] || '').length;
    const box = el.getBoundingClientRect();
    const offscreen = box.top > window.innerHeight;
    if (offscreen) render(el, 0, dec);
    let settled = false;
    setTimeout(() => { if (!settled) render(el, target, dec); }, 5000);
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (!e.isIntersecting) return;
        io.unobserve(el);
        settled = true;
        const start = performance.now();
        const dur = parseInt(el.getAttribute('data-count-dur') || '1500', 10);
        const tick = (now: number) => {
          const k = Math.min(1, (now - start) / dur);
          render(el, target * (1 - Math.pow(1 - k, 3)), dec);
          if (k < 1) requestAnimationFrame(tick);
          else render(el, target, dec);
        };
        requestAnimationFrame(tick);
      });
    }, { threshold: 0.4 });
    io.observe(el);
  });
}

function initRail() {
  const rail = document.querySelector<HTMLElement>('[data-rail]');
  const track = document.querySelector<HTMLElement>('[data-rail-track]');
  if (!rail || !track) return;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let distance = 0, ticking = false;
  const measure = () => {
    if (window.innerWidth <= 900 || reduced) {
      rail.style.height = '';
      track.style.transform = '';
      distance = 0;
      return;
    }
    distance = Math.max(0, track.scrollWidth - window.innerWidth + 40);
    rail.style.height = `${window.innerHeight + distance}px`;
    update();
  };
  const update = () => {
    if (!distance) { ticking = false; return; }
    const r = rail.getBoundingClientRect();
    const total = rail.offsetHeight - window.innerHeight;
    const p = total > 0 ? Math.min(1, Math.max(0, -r.top / total)) : 0;
    const dir = document.documentElement.dir === 'rtl' ? 1 : -1;
    track.style.transform = `translate3d(${dir * p * distance}px,0,0)`;
    ticking = false;
  };
  window.addEventListener('scroll', () => {
    if (!ticking) { ticking = true; requestAnimationFrame(update); }
  }, { passive: true });
  let rt: any;
  window.addEventListener('resize', () => { clearTimeout(rt); rt = setTimeout(measure, 160); }, { passive: true });
  track.querySelectorAll('img').forEach(img => {
    if (!(img as HTMLImageElement).complete) img.addEventListener('load', measure);
  });
  measure();
}

function initCapPeek() {
  const peek = document.querySelector<HTMLElement>('.cap-peek');
  const caps = Array.from(document.querySelectorAll<HTMLElement>('.cap'));
  if (!peek || !caps.length) return;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  if (reduced || !fine) return;
  const img = peek.querySelector<HTMLImageElement>('img');
  if (!img) return;
  let x = 0, y = 0, cx = 0, cy = 0, raf: number | null = null, active = false;
  caps.forEach(c => {
    const src = c.getAttribute('data-cap-img');
    if (src) { const pre = new Image(); pre.src = src; }
  });
  const loop = () => {
    cx += (x - cx) * 0.14;
    cy += (y - cy) * 0.14;
    peek.style.transform = `translate3d(${cx.toFixed(1)}px,${cy.toFixed(1)}px,0) translate(-50%,-50%)`;
    if (active || Math.abs(x - cx) > 0.5 || Math.abs(y - cy) > 0.5) raf = requestAnimationFrame(loop);
    else raf = null;
  };
  caps.forEach(c => {
    c.addEventListener('mouseenter', () => {
      const src = c.getAttribute('data-cap-img');
      if (src && img.getAttribute('src') !== src) img.setAttribute('src', src);
      active = true;
      peek.classList.add('on');
      if (!raf) raf = requestAnimationFrame(loop);
    });
    c.addEventListener('mouseleave', () => {
      active = false;
      peek.classList.remove('on');
    });
  });
  document.addEventListener('mousemove', (e: MouseEvent) => {
    x = e.clientX; y = e.clientY;
    if (active && !raf) raf = requestAnimationFrame(loop);
  }, { passive: true } as any);
}

function initFaqGroups() {
  document.querySelectorAll<HTMLElement>('[data-faq-group]').forEach(group => {
    const items = Array.from(group.querySelectorAll<HTMLDetailsElement>('.faq-item'));
    items.forEach(item => {
      item.addEventListener('toggle', () => {
        if (!item.open) return;
        items.forEach(o => { if (o !== item) o.open = false; });
      });
    });
  });
}

export function useMotion() {
  const { pathname } = useLocation();
  useEffect(() => {
    // document.documentElement.classList.add('js'); -- disabled: elements must be visible by default
    // small delay to let React render
    const t = setTimeout(() => {
      initScrollReveal();
      initLineReveal();
      initCounters();
      initRail();
      initCapPeek();
      initFaqGroups();
    }, 30);
    return () => { clearTimeout(t); };
  }, [pathname]);
}
